import { TABLE_CARD, TABLE_HEADER_ROW } from '@/utils/stickyColumns';
import type { FC } from 'react';

/**
 * Widths that make placeholder rows read as data rather than as a barcode.
 *
 * Cycled per column, so a four-column table gets four different bars and an eight-column one
 * repeats the rhythm instead of drawing eight identical blocks.
 */
const BAR_WIDTHS = ['w-32', 'w-24', 'w-20', 'w-28'];

/** The thumbnail block in the first column of the tools table, at its real size. */
const THUMBNAIL_CLASSES = 'h-[42px] w-[30px] shrink-0';

interface SkeletonBlockProps {
  /** Size and shape of this one block — `h-3 w-24`, `h-10 w-full`. Radius and colour are fixed. */
  className?: string;
}

/** One pulsing gray block. The unit every skeleton below is built from. */
export const SkeletonBlock: FC<SkeletonBlockProps> = ({ className = '' }) => (
  <span className={`block animate-pulse rounded bg-gray-100 ${className}`.trim()} />
);

interface TableSkeletonProps {
  /** How many placeholder rows. Match what the table usually shows, not what it can show. */
  rows: number;
  /** How many bars per row. */
  columns: number;
  /** Adds the tools table's thumbnail block at the start of every row. */
  withThumbnail?: boolean;
}

/**
 * Stand-in for a table while it loads.
 *
 * Shaped like the real thing — same card, same header strip, same row height — so the page does
 * not jump when the data arrives. A centred "Loading…" said less and moved everything under it.
 *
 * `aria-hidden` because it carries no information: the live region announcing the load belongs to
 * the page, not to a wall of gray bars.
 */
export const TableSkeleton: FC<TableSkeletonProps> = ({ rows, columns, withThumbnail = false }) => (
  <div className={TABLE_CARD} aria-hidden="true">
    <div className={TABLE_HEADER_ROW} />
    {Array.from({ length: rows }, (_, index) => index).map((rowIndex) => (
      <div key={rowIndex} className="flex h-12 items-center gap-4 border-b border-gray-100 px-3 last:border-b-0">
        {withThumbnail ? <SkeletonBlock className={THUMBNAIL_CLASSES} /> : null}
        {Array.from({ length: columns }, (_, index) => index).map((columnIndex) => (
          <SkeletonBlock key={columnIndex} className={`h-3 ${BAR_WIDTHS[columnIndex % BAR_WIDTHS.length]}`} />
        ))}
      </div>
    ))}
  </div>
);

interface PanelSkeletonProps {
  /** How many bars to stack. */
  lines: number;
}

/**
 * Stand-in for a panel of prose or key-value rows, for the places a table skeleton would be wrong.
 *
 * The last bar is short: a paragraph that ends mid-line reads as text, a stack of equal bars
 * reads as a loading graphic.
 */
export const PanelSkeleton: FC<PanelSkeletonProps> = ({ lines }) => (
  <div className="flex flex-col gap-2" aria-hidden="true">
    {Array.from({ length: lines }, (_, index) => index).map((index) => (
      <SkeletonBlock key={index} className={index === lines - 1 ? 'h-3 w-24' : 'h-3 w-full'} />
    ))}
  </div>
);
